import * as React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';

import { cn } from '@dfq/shared/lib/utils';

/**
 * Shared `Breadcrumb` capsule used by the Storefront `PublicLayout` and any
 * interior Console view that wants the same trail.
 *
 * Behaviour
 * - If `items` is supplied, renders them verbatim. Final segment is plain
 *   text (not a link); intermediate segments are `react-router-dom` `Link`s
 *   when they carry a `to`, otherwise plain text.
 * - If `items` is omitted, derives the trail from `useLocation().pathname`.
 *   The pathname is split by `/`, empty segments are dropped, and each
 *   remaining slug is mapped to a human-readable label via `CRUMB_LABELS`.
 *   Unknown slugs fall back to title-casing the slug (`how-to-order` →
 *   `How to Order`).
 * - Renders nothing on the root path `/` — home has no breadcrumb trail.
 *
 * Accessibility
 * - Wraps the list in a `<nav aria-label="Breadcrumb">` with a semantic
 *   `<ol>`, following the WAI-ARIA authoring practices.
 * - The final crumb sets `aria-current="page"`.
 *
 * Ticket: SF-INFRA-001 (#45). This component is a shared capsule so later
 * Console tickets can reuse it without a fork.
 */

/**
 * Static slug → label map. Add routes here as they land in the storefront
 * router (SF-INFRA-002) so breadcrumbs pick up the right text. Dynamic
 * segments (IDs / slugs) fall back to a title-cased slug until loader
 * context can inject a proper display name.
 */
const CRUMB_LABELS: Record<string, string> = {
  about: 'About',
  account: 'Account',
  cart: 'Cart',
  category: 'Products',
  checkout: 'Checkout',
  contact: 'Contact',
  faq: 'FAQ',
  gallery: 'Gallery',
  'how-it-works': 'How It Works',
  'how-to-order': 'How to Order',
  orders: 'Orders',
  privacy: 'Privacy Policy',
  'reset-password': 'Reset Password',
  'restore-account': 'Restore Account',
  products: 'Products',
  'full-wraps': 'Full Wraps',
  'cut-graphics': 'Cut Graphics',
  'window-film': 'Window Film',
  'design-services': 'Design Services',
  'partial-wraps': 'Partial Wraps',
  shipping: 'Shipping Info',
  'file-requirements': 'File Requirements',
  terms: 'Terms & Conditions',
};

export interface BreadcrumbItem {
  /** Visible label for the crumb. */
  label: string;
  /**
   * Destination path. Omit (or pass `undefined`) for the final crumb —
   * the component also renders the last crumb as plain text regardless
   * of whether `to` is set, to match WAI-ARIA guidance.
   */
  to?: string;
}

export interface BreadcrumbProps extends React.HTMLAttributes<HTMLElement> {
  /**
   * Explicit crumb items. When omitted, the component derives the trail
   * from `useLocation().pathname`.
   */
  items?: BreadcrumbItem[];
}

/** Title-case a slug (`how-to-order` → `How to Order`). */
function humanize(slug: string): string {
  return slug
    .split('-')
    .map((word) => (word.length > 0 ? word[0]!.toUpperCase() + word.slice(1) : word))
    .join(' ');
}

/**
 * Roots where `PublicLayout`'s auto-derived trail would duplicate (or
 * under-inform) a breadcrumb the route itself already renders, so this
 * capsule renders nothing there. Matched against the first path segment.
 *
 *  - `login` / `register` — full-bleed split-panel shells; a trail would
 *    only leave a gap below the nav.
 *  - `category` — `CategoryBrowseView` renders its own `Breadcrumb` built
 *    from real ancestor category names (e.g. "Cards & Stationery", not a
 *    slug-humanized "Cards Stationery"), shared with its JSON-LD
 *    `BreadcrumbList` structured data.
 */
const BREADCRUMBLESS_ROOTS = new Set(['login', 'register', 'category']);

/** Roots whose next segment is a single-use emailed token — never print it on the page. */
const TOKEN_ROOTS = new Set(['reset-password', 'restore-account']);

function deriveItemsFromPathname(pathname: string): BreadcrumbItem[] {
  const allSegments = pathname.split('/').filter((segment) => segment.length > 0);
  if (allSegments.length === 0) return [];
  if (BREADCRUMBLESS_ROOTS.has(allSegments[0]!)) return [];
  const segments = TOKEN_ROOTS.has(allSegments[0]!) ? allSegments.slice(0, 1) : allSegments;

  const items: BreadcrumbItem[] = [];
  let href = '';
  for (let i = 0; i < segments.length; i += 1) {
    const segment = segments[i]!;
    href += `/${segment}`;
    const label = CRUMB_LABELS[segment] ?? humanize(segment);
    items.push({
      label,
      // Last crumb is rendered as plain text regardless of `to` presence.
      to: i === segments.length - 1 ? undefined : href,
    });
  }
  return items;
}

export function Breadcrumb({ items, className, ...rest }: BreadcrumbProps) {
  const location = useLocation();
  const resolvedItems = React.useMemo(() => {
    if (items) return items;
    return deriveItemsFromPathname(location.pathname);
  }, [items, location.pathname]);

  if (resolvedItems.length === 0) {
    return null;
  }

  return (
    <nav
      aria-label="Breadcrumb"
      className={cn('text-body-small text-text-secondary', className)}
      {...rest}
    >
      <ol className="flex flex-wrap items-center gap-1.5">
        {resolvedItems.map((item, index) => {
          const isLast = index === resolvedItems.length - 1;
          return (
            <li key={`${item.label}-${index}`} className="inline-flex items-center gap-1.5">
              {index > 0 ? (
                <ChevronRight
                  aria-hidden="true"
                  className="size-3.5 text-text-secondary/70"
                />
              ) : null}
              {isLast || !item.to ? (
                <span
                  aria-current={isLast ? 'page' : undefined}
                  className={cn(isLast && 'font-medium text-text-primary')}
                >
                  {item.label}
                </span>
              ) : (
                <Link
                  to={item.to}
                  className="rounded-sm underline-offset-4 transition-colors hover:text-text-primary hover:underline focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
                >
                  {item.label}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
